
import { useState } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface UpgradeOptions {
  plan?: 'premium' | 'church';
  redirectToCheckout?: boolean;
}

interface CheckoutError {
  message: string;
  code?: string;
}

/**
 * Hook for handling premium plan upgrades
 * Separated from UI components for better reusability
 */
export function usePremiumUpgrade() {
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<CheckoutError | null>(null);
  const session = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  /**
   * Prepares user session for checkout
   * @returns boolean indicating if session is valid
   */
  const prepareForCheckout = (): boolean => {
    // Scroll to top using more reliable methods
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
    
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (!session?.access_token) {
      toast({
        title: "Authentication required",
        description: "Please sign in to upgrade your plan.",
        variant: "destructive",
      });
      navigate('/auth');
      return false;
    }
    
    return true;
  };

  /**
   * Creates checkout session with Supabase Edge Function
   */
  const createCheckoutSession = async (options?: UpgradeOptions) => {
    setCheckoutError(null);
    
    if (!prepareForCheckout()) {
      return null;
    }

    try {
      setIsLoading(true);
      const planType = options?.plan || 'premium';
      console.log('Initiating checkout process for', planType);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          plan: planType
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Checkout error:', error);
        setCheckoutError({
          message: error.message || "Unable to start checkout process",
          code: error.name
        });
        throw error;
      }

      if (!data?.url) {
        const noUrlError = new Error('Invalid checkout response');
        setCheckoutError({
          message: noUrlError.message,
          code: 'INVALID_RESPONSE'
        });
        throw noUrlError;
      }

      return data.url;
    } catch (error: any) {
      console.error('Checkout failed:', error);
      setCheckoutError({
        message: error.message || "Unable to start checkout process",
        code: error.code
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initiates upgrade process with optional redirect
   */
  const handleUpgrade = async (options?: UpgradeOptions) => {
    const redirectToCheckout = options?.redirectToCheckout !== false;
    const checkoutUrl = await createCheckoutSession(options);
    
    if (checkoutUrl && redirectToCheckout) {
      console.log('Redirecting to checkout');
      window.location.href = checkoutUrl;
    }
    
    return checkoutUrl;
  };

  /**
   * Convenience method for premium plan upgrade
   */
  const handlePremiumUpgrade = async (redirectToCheckout = true) => {
    return handleUpgrade({ 
      plan: 'premium',
      redirectToCheckout
    });
  };

  /**
   * Convenience method for church plan upgrade
   */
  const handleChurchUpgrade = async (redirectToCheckout = true) => {
    return handleUpgrade({ 
      plan: 'church',
      redirectToCheckout
    });
  };

  return { 
    isLoading, 
    error: checkoutError,
    clearError: () => setCheckoutError(null),
    handleUpgrade,
    handlePremiumUpgrade,
    handleChurchUpgrade,
    createCheckoutSession
  };
}
